
/**
 * TODO: Documentation
 * `HotspotSimple` - Component to place hotspots on an image.
 * Uses GenericImage Component to display image.
 */

import GenericImage from '@owc-packages/GenericImage/src/GenericImage.vue';
import { HotspotSimpleComponent, HotspotSidepanelData } from '@owc-packages/Hotspot/src/HotspotSimple.d';
import SlotContainer from '@owc-packages/SlotContainer/src/SlotContainer.vue';
import ValidationBoxWrapper from '@owc-packages/ValidationBox/src';
import { HotspotState } from '@owc-packages/_types/Hotspot';
import HotspotMixin from '@owc-packages/_utils/Mixins/HotspotMixin';
import { mobileBreakpoints } from '@owc-utils/MediaQueryService';
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import HotspotOverlay from './HotspotOverlay.vue';
import HotspotSidepanel from './HotspotSidepanel.vue';
import HotspotSimpleItem from './HotspotSimpleItem.vue';
import HotspotSimpleAuthorItem from './HotspotSimpleAuthorItem.vue';
import { calculateWalkDistance, customItemTrackingTrigger, setFootnotes } from './HotspotUtils';
import { HOTSPOT_SIMPLE_ITEM_TYPE } from './HotspotSimple.enums';

const IMAGE_TRANSLATE_PROP_NAME = '--image-translate-x';

export default (ComponentInitializeDispatcher as HotspotSimpleComponent).extend({
  name: 'owc-hotspot-simple',
  mixins: [HotspotMixin],
  components: {
    GenericImage,
    SlotContainer,
    ValidationBoxWrapper,
    HotspotOverlay,
    HotspotSidepanel,
    [HOTSPOT_SIMPLE_ITEM_TYPE.PUBLISH]: HotspotSimpleItem,
    [HOTSPOT_SIMPLE_ITEM_TYPE.AUTHOR]: HotspotSimpleAuthorItem,
  },
  props: {
    noOverlay: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    isMobile: false,
    isHotspotSidepanelActive: false,
    activeHotspot: null,
    overlayShown: false,
    isRtl: false,
  }),
  computed: {
    availableHotspot() {
      return (this.componentData?.items || []).map(item => ({
        ...item,
        sidepanel: { ...item.sidepanel, component: this },
        id: item.identifiers.componentId,
      }));
    },
    genericImage() {
      return this.componentData?.image;
    },
    shouldRenderOverlay() {
      // Force the overlay to be hidden, e.g. for visual tests
      if (this.noOverlay) {
        return false;
      }
      return Boolean(this.overlay) && this.isMobile && !this.isEditMode && !this.overlayShown;
    },
    overlay() {
      return this.componentData?.overlay;
    },
    isDarkMode() {
      return Boolean(this.componentData?.darkModeOverlayBackground);
    },
    isSidepanelOpen() {
      return Boolean(this.activeHotspot) && this.isHotspotSidepanelActive;
    },
    activeHotspotIndex() {
      if (this.activeHotspot) {
        return this.availableHotspot.findIndex(
          ({ id }: HotspotSidepanelData) => id === this.activeHotspot?.id,
        );
      }
      return 0;
    },
    leftHotspot() {
      if (!this.activeHotspot) {
        return null;
      }
      let leftHotspotIndex = this.activeHotspotIndex - 1;
      if (leftHotspotIndex < 0) {
        leftHotspotIndex = this.availableHotspot.length - 1;
      }
      return this.availableHotspot[leftHotspotIndex];
    },
    rightHotspot() {
      if (!this.activeHotspot) {
        return null;
      }
      const rightHotspotIndex = (this.activeHotspotIndex + 1) % this.availableHotspot.length;

      return this.availableHotspot[rightHotspotIndex];
    },
    itemElementType() {
      return this.isEditMode ? HOTSPOT_SIMPLE_ITEM_TYPE.AUTHOR : HOTSPOT_SIMPLE_ITEM_TYPE.PUBLISH;
    },
  },
  mounted() {
    this.customItemTrackingTrigger = customItemTrackingTrigger();
    this.onMQChange?.(this.setIsMobileFlag);

    this.setIsMobileFlag();
    this.scrollToCenter();

    this.addHotspotChangeListener(this.getComponentId(), this.findActiveHotspot);

    this.isRtl = !!this.$el.closest('[dir="rtl"]');
  },
  beforeDestroy() {
    this.removeHotspotChangeListener(this.getComponentId(), this.findActiveHotspot);
  },
  methods: {
    onLeftHotspotActivated() {
      this.deactivateHotspotTooltip();
      this.activeHotspot = this.leftHotspot;
    },
    onRightHotspotActivated() {
      this.deactivateHotspotTooltip();
      this.activeHotspot = this.rightHotspot;
    },
    hotspotImageTransitioned(transitionEvent: TransitionEvent) {
      if (transitionEvent.target === this.$refs.imgContainer && this.activeHotspot) {
        this.setHotspotItem(this.activeHotspot?.id);
      }
    },
    openHotspotOverlay() {
      this.componentId && this.hideHotspotTabs(this.componentId);
    },
    closeHotspotOverlay() {
      this.componentId && this.showHotspotTabs(this.componentId);
      this.overlayShown = true;
    },
    onCloseSidepanelClick() {
      this.resetImagePosition();
      this.setHotspotItem(null);
      this.activeHotspot = null;
      this.isHotspotSidepanelActive = false;
    },
    /**
     * scrollToCenter
     * Will scroll image, so the scroll bar in the image container is centered.
     * Only takes effect from screen width MQ4 and down.
     */
    scrollToCenter() {
      const imageContainer = this.$refs.imgContainer as Element;
      const clientWidth = imageContainer.clientWidth;
      const scrollWidth = imageContainer.scrollWidth;
      if (scrollWidth > clientWidth) {
        imageContainer.scrollLeft = (scrollWidth - clientWidth) / 2;
      }
    },
    /**
     * centerImageIfNecessary
     * will position the hotspot on horizontal center of the remaining
     * part of the screen (not covered by the sidepanel)
     */
    centerImageIfNecessary() {
      if (!this.activeHotspot) {
        return;
      }
      const imgContainer = this.$refs.imgContainer as HTMLElement;
      const walkDistance = calculateWalkDistance(
        imgContainer,
        this.$el,
        this.activeHotspot.coordinateX,
        this.isRtl,
      );
      const currentTranslateX = imgContainer.style.getPropertyValue(IMAGE_TRANSLATE_PROP_NAME);

      // OWCA-386: in some rare cases, when two (or more) items have x-coordinate near
      // to the image edge, the walk distance will be the same for all these items.
      // In such a case the transition doesn't take place, hence transitionend event
      // is not fired, so we must set active hotspot id manually.
      if (walkDistance === parseInt(currentTranslateX, 10)) {
        this.setActiveHotspotId(this.componentId, this.activeHotspot.id);
      }

      imgContainer.style.setProperty(IMAGE_TRANSLATE_PROP_NAME, String(walkDistance));
    },
    resetImagePosition() {
      const imgContainer = this.$refs.imgContainer as HTMLElement;
      imgContainer.style.setProperty(IMAGE_TRANSLATE_PROP_NAME, '0');
    },
    setIsMobileFlag() {
      const currentMq: string = this.getCurrentMediaQuery?.() || '';
      this.isMobile = mobileBreakpoints.includes(currentMq);

      this.centerImageIfNecessary();
    },
    findActiveHotspot(state: HotspotState) {
      const activeHotspotId = this.componentId && state.activeItemsMap[this.componentId];

      // No active hotspot item id for the given module, just unset data and skip
      if (!activeHotspotId) {
        this.activeHotspot = null;
        this.isHotspotSidepanelActive = false;
        return;
      }

      const nextActiveHotspot = this.availableHotspot.find(
        ({ id }: HotspotSidepanelData) => id === activeHotspotId,
      );

      if (nextActiveHotspot) {
        this.activeHotspot = nextActiveHotspot;
        this.isHotspotSidepanelActive = true;
        this.customItemTrackingTrigger(this.$el, this.activeHotspot);
      }
    },
    /**
     * Will deactivate the tooltip when the image is moving
     * to the next hotspot item. If the tooltip is active
     * while the image is moving it looks like a bug
     */
    deactivateHotspotTooltip() {
      // set the active hotspot id to a non-existing value but NOT null!
      // this will toogle the tooltip. Check out HotspotSimpleItem.vue for
      // more details
      if (this.activeHotspot) {
        this.setHotspotItem(`${this.activeHotspot.id}-hidden-tooltip`);
      }
    },
    setHotspotItem(itemId) {
      this.setActiveHotspotId(this.componentId, itemId);
    },
  },
  watch: {
    activeHotspot(hotspot) {
      if (hotspot) {
        this.componentId && this.hideHotspotTabs(this.componentId);
        this.centerImageIfNecessary();
      } else {
        this.componentId && this.showHotspotTabs(this.componentId);
      }
    },
    availableHotspot: {
      handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          setFootnotes(newValue);
        }
      },
      immediate: true,
    },
  },
});
